import { IonIcon } from '@ionic/react';
import { chevronDownOutline, chevronForwardOutline } from 'ionicons/icons';
import clsx from 'clsx';
import React from 'react';
import { BonusReward } from 'services/casino';
import { S3_CASINOS_BASE } from 'common/common';

const S3_CASINOS = S3_CASINOS_BASE;

interface RewardItem {
  rank: number;
  amount: number;
  amountType: string;
  badge?: 'gold' | 'silver' | 'bronze' | 'numbered';
}

interface BonusItem {
  label: string;
  image?: string;
}

interface LeaderWinningsProps {
  title?: string;
  subtitle?: string;
  dateRange?: string;
  notice?: string;
  amount?: number | string;
  label?: string;
  rewards?: RewardItem[];
  footerNote?: string;
  bonusTitle?: string;
  bonusSubtitle?: string;
  bonusItems?: BonusItem[];
  bonusRewards?: BonusReward;
  styleBackground?: string;
  styleName?: string;
  className?: string;
  style?: React.CSSProperties;
  defaultExpanded?: boolean;
}

const toOrdinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

const BADGE_SVG: Record<
  'gold' | 'silver' | 'bronze' | 'numbered' | 'goldenSlot' | 'goldenHandle' | 'worldTraveler',
  string
> = {
  gold: 'assets/images/rank-gold.svg',
  silver: 'assets/images/rank-silver.svg',
  bronze: 'assets/images/rank-bronze.svg',
  numbered: 'assets/images/rank-badge.svg',
  goldenSlot: 'assets/images/badge-golden-slot.svg',
  goldenHandle: 'assets/images/badge-golden-handle.svg',
  worldTraveler: 'assets/images/badge-world-traveler.svg',
};

const defaultBonusItems: BonusItem[] = [
  { label: 'Most Jackpots', image: 'assets/images/badge-jackpot.png' },
  { label: 'Money Won', image: 'assets/images/badge-money.png' },
  { label: 'Money Won / Spin', image: 'assets/images/badge-win-spin.png' },
  { label: 'Most Spins', image: 'assets/images/badge-spin.png' },
];

const formatAmount = (amount: number | string | undefined): string => {
  if (amount === undefined || amount === null || String(amount).trim() === '') {
    return '$10,750';
  }
  if (typeof amount === 'number') {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  return String(amount);
};

const LeaderWinnings: React.FC<LeaderWinningsProps> = ({
  title = '',
  subtitle = '',
  dateRange = '',
  notice = '',
  amount = 0,
  label = 'Winnings',
  rewards = [],
  footerNote = '',
  bonusTitle = '',
  bonusSubtitle = '',
  bonusItems = defaultBonusItems,
  bonusRewards,
  styleBackground = '',
  styleName = '',
  className = '',
  style,
  defaultExpanded = false,
}) => {
  const styleFolder = styleBackground?.split('/').filter(Boolean)[0] ?? '';

  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  return (
    <div
      className={clsx(
        'leaderboard-winnings-card',
        !isExpanded && 'leaderboard-winnings-card--collapsed',
        className
      )}
      style={style}
    >
      <div className="leaderboard-winnings-card__header">
        <button
          type="button"
          className="leaderboard-winnings-card__toggle-row"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
        >
          <span className="leaderboard-winnings-card__label">{label}</span>
          <IonIcon
            icon={isExpanded ? chevronDownOutline : chevronForwardOutline}
            className="leaderboard-winnings-card__toggle-icon"
          />
        </button>
        {/* <button
          type="button"
          className="leaderboard-winnings-card__close"
          onClick={closeCard}
          aria-label="Close winnings card"
        >
          <IonIcon icon={closeOutline} />
        </button> */}
      </div>

      {!isExpanded && (
        <div className="leaderboard-winnings-card__main-pill leaderboard-winnings-card__main-pill--collapsed">
          <div className="leaderboard-winnings-card__pill-label">WINNINGS</div>
          <div className="leaderboard-winnings-card__pill-value">{formatAmount(amount)}</div>
        </div>
      )}

      <div
        className={clsx(
          'leaderboard-winnings-card__body',
          isExpanded
            ? 'leaderboard-winnings-card__body--visible'
            : 'leaderboard-winnings-card__body--hidden'
        )}
      >
        <div className="leaderboard-winnings-card__title">{title}</div>
        <div className="leaderboard-winnings-card__subtitle">{subtitle}</div>
        <div className="leaderboard-winnings-card__date-range">{dateRange}</div>
        <div className="leaderboard-winnings-card__notice">{notice}</div>

        <div className="leaderboard-winnings-card__main-pill">
          <div className="leaderboard-winnings-card__pill-label">WINNINGS</div>
          <div className="leaderboard-winnings-card__pill-value">{formatAmount(amount)}</div>
        </div>

        <div className="leaderboard-winnings-card__section-title">REWARDS</div>
        <div className="leaderboard-winnings-card__rewards-list">
          {rewards.map((reward) => (
            <div key={reward.rank} className="leaderboard-winnings-card__reward-item">
              {reward.badge != null && (
                <div className="leaderboard-winnings-card__reward-badge">
                  <img
                    src={BADGE_SVG[reward.badge]}
                    alt={toOrdinal(reward.rank)}
                    className="leaderboard-winnings-card__reward-badge-img"
                  />
                  {reward.badge === 'numbered' && (
                    <span className="leaderboard-winnings-card__reward-badge-num">
                      {reward.rank}
                    </span>
                  )}
                </div>
              )}
              <div className="leaderboard-winnings-card__reward-rank">
                {toOrdinal(reward.rank)}-
              </div>
              <div className="leaderboard-winnings-card__reward-text">
                ${reward.amount} {reward.amountType}
              </div>
            </div>
          ))}
        </div>

        {footerNote != null && footerNote !== '' && (
          <div className="leaderboard-winnings-card__footer-note">{footerNote}</div>
        )}

        {bonusRewards && (
          <div className="leaderboard-winnings-card__bonus-rewards-section">
            {bonusRewards.title && (
              <div className="leaderboard-winnings-card__section-title">{bonusRewards.title}</div>
            )}

            {bonusRewards.descriptionLines && bonusRewards.descriptionLines.length > 0 && (
              <div className="leaderboard-winnings-card__bonus-rewards-description-line">
                {bonusRewards.descriptionLines.map((l) => (
                  <div key={l.text}>{l.text}</div>
                ))}
              </div>
            )}

            {bonusRewards.rewards && bonusRewards.rewards.length > 0 && (
              <div className="leaderboard-winnings-card__bonus-rewards-list">
                {bonusRewards.rewards.map((reward, ri) => (
                  <div key={ri} className="leaderboard-winnings-card__bonus-rewards-item">
                    <div className="leaderboard-winnings-card__bonus-rewards-header">
                      <div className="leaderboard-winnings-card__bonus-rewards-icon">
                        <img
                          src={`${S3_CASINOS}/${styleName}/${styleFolder}/rewards/${reward.badge}.svg`}
                          alt={reward.badgeText}
                          className="leaderboard-winnings-card__bonus-rewards-icon-img"
                        />
                      </div>
                      <div className="leaderboard-winnings-card__bonus-rewards-title">
                        {reward.badgeText}
                        {reward.amount != null &&
                          ` – ${reward.amountSymbol ?? '$'}${reward.amount.toLocaleString()}${
                            reward.amountType ? ` ${reward.amountType}` : ''
                          }`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {bonusRewards.bonusRewarditems && bonusRewards.bonusRewarditems.length > 0 && (
              <div className="leaderboard-winnings-card__bonus-rewards-list">
                {bonusRewards.bonusRewarditems.map((item, ii) => (
                  <div key={ii} className="leaderboard-winnings-card__bonus-rewards-item">
                    <div className="leaderboard-winnings-card__bonus-rewards-header">
                      <div className="leaderboard-winnings-card__bonus-rewards-icon">
                        <img
                          src={`${S3_CASINOS}/${styleName}/${styleFolder}/rewards/${item.badge}.svg`}
                          alt={item.badgeText}
                          className="leaderboard-winnings-card__bonus-rewards-icon-img"
                        />
                      </div>
                      <div className="leaderboard-winnings-card__bonus-rewards-title">
                        {item.badgeText}
                        {item.amount != null &&
                          ` – ${item.amountSymbol ?? '$'}${item.amount.toLocaleString()}${
                            item.amountType ? ` ${item.amountType}` : ''
                          }`}
                      </div>
                    </div>
                    {item.text && (
                      <div className="leaderboard-winnings-card__bonus-rewards-desc">
                        {item.text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="leaderboard-winnings-card__section-title">{bonusTitle}</div>
        <div className="leaderboard-winnings-card__bonus-subtitle">
          {bonusSubtitle ? <>&ldquo;{bonusSubtitle}&rdquo;</> : ''}
        </div>
        <div className="leaderboard-winnings-card__bonus-subtitle"></div>
        <div className="leaderboard-winnings-card__bonus-list">
          {bonusItems.map((item) => (
            <div key={item.label} className="leaderboard-winnings-card__bonus-item">
              <div className="leaderboard-winnings-card__bonus-icon">
                {item.image != null && (
                  <img
                    src={item.image}
                    alt={item.label}
                    className="leaderboard-winnings-card__bonus-icon-img"
                  />
                )}
              </div>
              <div className="leaderboard-winnings-card__bonus-label">{item.label}</div>
            </div>
          ))}
        </div>

        <div className="leaderboard-winnings-card__footer">See player Services for Rules</div>
      </div>
    </div>
  );
};

export default React.memo(LeaderWinnings);
